import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useFetch } from '@/hooks/use-fetch';

export interface DashboardMetrics {
  channels: number;
  /** Usage over the selected range, in `usageUnit` (AIT-436). */
  used: number;
  usageUnit: 'messages' | 'actions';
  daily: Array<{ date: string; count: number }>;
}
// 'period' = the org's current quota window (AIT-629), so the dashboard adds
// up to the same number as the sidebar meter.
export type RangePreset = 'period' | 'today' | '7d' | '30d' | 'custom';
// For 'custom', the caller supplies UTC calendar dates 'YYYY-MM-DD'.
export interface CustomDates { start: string; end: string }
// Page-level filter scoping every dashboard metric to channels of a lifecycle
// status. 'active' = connected channels; 'all' is the default (no scoping).
export type ChannelStatusFilter = 'all' | 'active' | 'disconnected';

// UTC, half-open [from, to). Presets: today plus previous N-1 days, to = now.
// Custom: from = trunc(start), to = min(trunc(end)+1d, now). The +1d includes
// the end day fully, but for a range ending today that lands ~24h in the future
// and the backend rejects a `to` more than a few minutes ahead, so clamp to now.
export function rangeFor(
  preset: RangePreset, custom?: CustomDates, periodStart?: string,
): { from: string; to: string } {
  const now = new Date();
  // 'period' without a known window start (trial, old backend) falls back to 7d.
  if (preset === 'period' && periodStart) {
    return { from: new Date(periodStart).toISOString(), to: now.toISOString() };
  }
  const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (preset === 'custom' && custom) {
    const from = new Date(`${custom.start}T00:00:00Z`);
    const end = new Date(`${custom.end}T00:00:00Z`);
    const to = new Date(Math.min(end.getTime() + 86_400_000, now.getTime()));
    return { from: from.toISOString(), to: to.toISOString() };
  }
  const back = preset === 'today' ? 0 : preset === '30d' ? 29 : 6; // '7d' default; 'custom' w/o dates falls back to 7d
  return { from: new Date(todayStart.getTime() - back * 86_400_000).toISOString(), to: now.toISOString() };
}

// dispatch is not tracked by react-hooks/set-state-in-effect, so a synchronous
// start dispatch in the effect path is safe.
type MetricsState = { data: DashboardMetrics | null; loaded: boolean };
type MetricsAction = { type: 'start' } | { type: 'reset' } | { type: 'done'; data: DashboardMetrics | null };
function metricsReducer(state: MetricsState, action: MetricsAction): MetricsState {
  switch (action.type) {
    case 'start': return { ...state, loaded: false };
    // Waiting for a new quota window: drop the previous org/workspace's numbers
    // so a failed usage fetch cannot leave them on screen (AIT-629).
    case 'reset': return { data: null, loaded: false };
    case 'done': return { data: action.data, loaded: true };
  }
}

export function useDashboardMetrics(
  preset: RangePreset, custom?: CustomDates, status: ChannelStatusFilter = 'all', periodStart?: string,
) {
  const fetchWithAuth = useFetch();
  const [{ data, loaded }, dispatch] = useReducer(metricsReducer, { data: null, loaded: false });

  // Extract primitives so exhaustive-deps is satisfied without capturing the
  // whole `custom` object (which can be recreated each render by the parent).
  const start = custom?.start;
  const end = custom?.end;

  // AIT-629: `periodStart` arrives async, so a cold 'period' load fires 7d
  // first and the real range second. Only the latest request may dispatch.
  const seq = useRef(0);
  const load = useCallback(async () => {
    // 'custom' with incomplete dates: skip the fetch until both are picked.
    // This early return must come BEFORE any dispatch so the skip path issues
    // neither a request nor a 'start' action.
    if (preset === 'custom' && (!start || !end)) return;
    // 'period' before the usage response: wait rather than show 7d totals
    // under a "Billing period" tab (the org-usage query is fail-soft, so a
    // failure here keeps the skeleton instead of lying).
    if (preset === 'period' && !periodStart) {
      ++seq.current; // an older in-flight response must not land either
      dispatch({ type: 'reset' });
      return;
    }
    const mine = ++seq.current;
    dispatch({ type: 'start' });
    const { from, to } = rangeFor(preset, start && end ? { start, end } : undefined, periodStart);
    // The metrics panel is a non-critical widget: a 5xx/network blip on its
    // read must render empty metrics, NOT escalate to the global ErrorPage and
    // tear the whole dashboard (header + sidebar) down. suppressGlobalError +
    // a local catch is the codebase's fail-soft contract (see use-fetch.ts).
    try {
      const res = await fetchWithAuth(
        `/dashboard/metrics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&status=${status}`,
        undefined,
        { suppressGlobalError: true },
      );
      // dispatch is stable across renders (React guarantee) — no need to list it in deps.
      const data = res.ok ? await res.json() : null;
      if (mine === seq.current) dispatch({ type: 'done', data });
    } catch {
      if (mine === seq.current) dispatch({ type: 'done', data: null });
    }
  }, [fetchWithAuth, preset, start, end, status, periodStart]);

  useEffect(() => { void load(); }, [load]);

  // Derive loading: true on initial mount until first fetch resolves, false
  // immediately when the skip condition is active (no request will be issued).
  const skip = preset === 'custom' && (!start || !end);
  // 'period' without a start is not a skip: the usage response is on its way,
  // so stay in loading (skeleton) instead of rendering zeros.
  return { data, loading: !loaded && !skip, reload: load };
}
